import {
  DisclosureFormStatus,
  DisclosureSubmissionStatus,
  type DisclosureType,
  FormItemType,
} from '../enums'

/**
 * The place of each status in a form's life; lists sort by status in this order. A record, so a
 * status added to the enum but not here fails to compile instead of sorting unranked.
 */
export const DISCLOSURE_FORM_STATUS_LIFECYCLE_RANK: Record<DisclosureFormStatus, number> = {
  [DisclosureFormStatus.Draft]: 0,
  [DisclosureFormStatus.Published]: 1,
  [DisclosureFormStatus.Closed]: 2,
  [DisclosureFormStatus.Archived]: 3,
}

/** An archived form is terminal — there is nothing left to preview or share. */
export const isDisclosureFormPreviewable = (status: DisclosureFormStatus) =>
  status !== DisclosureFormStatus.Archived

/** Only a Draft form may still be edited. */
export const isDisclosureFormLocked = (status: DisclosureFormStatus) =>
  status !== DisclosureFormStatus.Draft

export const isDisclosureFormAcceptingSubmissions = (status: DisclosureFormStatus) =>
  status === DisclosureFormStatus.Published

/**
 * Auto-approval only evaluates answers picked from fixed options; free text and the other types
 * are never part of the decision.
 */
export const autoApprovalDisclosureFormItemTypes = [
  FormItemType.Select,
  FormItemType.YesNo,
  FormItemType.MultiSelect,
] as const

export const isAutoApprovalDisclosureFormItemType = (
  type: FormItemType
): type is (typeof autoApprovalDisclosureFormItemTypes)[number] =>
  (autoApprovalDisclosureFormItemTypes as readonly FormItemType[]).includes(type)

/**
 * Auto-approval rules and the toggle stay editable after publishing, unlike the rest of the form,
 * so an officer can stop or correct the automation without abandoning the form.
 */
export const isDisclosureFormAutoApprovalEditable = (status: DisclosureFormStatus) =>
  status === DisclosureFormStatus.Draft || status === DisclosureFormStatus.Published

export const isDisclosureFormOpenForInviteDeletion = (status: DisclosureFormStatus) =>
  status === DisclosureFormStatus.Draft || status === DisclosureFormStatus.Published

/** Disclosure type is the only field required to publish a form. */
export const isDisclosureFormPublishable = (type: DisclosureType | null) => type !== null

export const isDisclosureSubmissionRemindable = ({
  formStatus,
  submissionStatus,
}: {
  formStatus: DisclosureFormStatus
  submissionStatus: DisclosureSubmissionStatus
}) =>
  formStatus === DisclosureFormStatus.Published &&
  submissionStatus === DisclosureSubmissionStatus.Pending

/** Mirrors the backend guard in `DisclosureInviteDeleteService`. */
export const isDisclosureInviteDeletable = ({
  formStatus,
  submissionStatus,
}: {
  formStatus: DisclosureFormStatus
  submissionStatus: DisclosureSubmissionStatus
}) =>
  isDisclosureFormOpenForInviteDeletion(formStatus) &&
  (submissionStatus === DisclosureSubmissionStatus.Pending ||
    submissionStatus === DisclosureSubmissionStatus.DeliveryFailed)

/**
 * The place of each status in the submission lists: the rows that wait for the officer come
 * first and the finished ones last. A record, so a status added to the enum but not here fails
 * to compile instead of sorting unranked.
 */
export const DISCLOSURE_SUBMISSION_STATUS_LIST_RANK: Record<DisclosureSubmissionStatus, number> = {
  [DisclosureSubmissionStatus.DeliveryFailed]: 0,
  [DisclosureSubmissionStatus.NeedsApproval]: 1,
  [DisclosureSubmissionStatus.UnderReview]: 2,
  [DisclosureSubmissionStatus.Pending]: 3,
  [DisclosureSubmissionStatus.NotSubmitted]: 4,
  [DisclosureSubmissionStatus.Declined]: 5,
  [DisclosureSubmissionStatus.Approved]: 6,
  [DisclosureSubmissionStatus.AutoApproved]: 7,
}

/**
 * Notes with content are stored exactly as typed, not trimmed: the field saves while the member
 * types, so trimming would eat the space they just typed. Only blank notes become `null`.
 */
export const toStoredDisclosureSubmissionNotes = (notes: string) =>
  notes.trim() === '' ? null : notes
